import './EliminationDiagram.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { activeBracketFormatAtom, bracketDiagramAtom } from './eliminationState.ts';
import type { BracketNodeViewModel } from './eliminationState.ts';
import { currentRaceAtom } from '../race/race-atoms.ts';
import type { PBRaceRecord } from '../api/pbTypes.ts';

interface ViewportState {
	scale: number;
	x: number;
	y: number;
}

const MIN_SCALE = 0.1;
const MAX_SCALE = 1.6;
const BASE_NODE_SLOT_COUNT = 4;
const EXTRA_SLOT_HEIGHT_PX = 40;
const DESTINATION_COLUMN_WIDTH = '12rem';

function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

function getNodeHeight(
	node: BracketNodeViewModel,
	defaultNodeHeight: number,
): number {
	const displayedSlotCount = Math.max(
		node.definition.slotCount,
		node.slots.length,
	);
	const extraSlotCount = Math.max(0, displayedSlotCount - BASE_NODE_SLOT_COUNT);
	return defaultNodeHeight + extraSlotCount * EXTRA_SLOT_HEIGHT_PX;
}

export function EliminationDiagram() {
	const diagram = useAtomValue(bracketDiagramAtom);
	const activeFormat = useAtomValue(activeBracketFormatAtom);
	const currentRace = useAtomValue(currentRaceAtom);
	const containerRef = useRef<HTMLDivElement>(null);
	const pointerState = useRef<{
		id: number | null;
		startX: number;
		startY: number;
		originX: number;
		originY: number;
		lastX: number;
		lastY: number;
		lastTime: number;
		velocityX: number;
		velocityY: number;
	}>({
		id: null,
		startX: 0,
		startY: 0,
		originX: 0,
		originY: 0,
		lastX: 0,
		lastY: 0,
		lastTime: 0,
		velocityX: 0,
		velocityY: 0,
	});
	const momentumAnimation = useRef<number | null>(null);

	// Calculate initial viewport based on current race
	const getInitialViewport = (): ViewportState => {
		// Find the node corresponding to the current race
		const currentNode = currentRace ? diagram.nodes.find((node) => node.raceIds.includes(currentRace.id)) : null;

		// Fallback to first node if no current race
		const targetNode = currentNode ?? diagram.nodes[0];

		if (!targetNode) {
			return { scale: 1.0, x: 80, y: 60 };
		}

		// Center on the target node
		const nodeX = targetNode.definition.position.x;
		const nodeY = targetNode.definition.position.y;
		const scale = 1.0;
		const nodeWidth = activeFormat.diagramDimensions.nodeWidth;
		const containerWidth = containerRef.current?.clientWidth ??
			(typeof globalThis.innerWidth === 'number' ? globalThis.innerWidth : 0);

		// Center horizontally and apply a small desktop left-of-center bias only when there is ample room.
		const centeredX = containerWidth > 0 ? (containerWidth - nodeWidth * scale) / 2 : 100;
		const desktopBias = containerWidth >= 900 ? -80 : 0;
		const x = -(nodeX * scale) + centeredX + desktopBias;
		const y = -(nodeY * scale) + 200;

		return { scale, x, y };
	};

	const [viewport, setViewport] = useState<ViewportState>(getInitialViewport);
	const [isDragging, setIsDragging] = useState(false);
	const [isAtDefault, setIsAtDefault] = useState(true);
	const [pinchState, setPinchState] = useState<
		{
			initialDistance: number;
			initialScale: number;
			initialCenterX: number;
			initialCenterY: number;
			initialViewportX: number;
			initialViewportY: number;
		} | null
	>(null);

	// Track if viewport is at default position
	const checkIfAtDefault = (vp: ViewportState): boolean => {
		const initial = getInitialViewport();
		return (
			vp.scale === initial.scale && vp.x === initial.x && vp.y === initial.y
		);
	};
	const checkIfAtDefaultRef = useRef(checkIfAtDefault);

	useEffect(() => {
		checkIfAtDefaultRef.current = checkIfAtDefault;
	}, [checkIfAtDefault]);

	// Auto-follow current race when at default position
	useEffect(() => {
		if (isAtDefault) {
			const newViewport = getInitialViewport();
			setViewport(newViewport);
		}
	}, [currentRace?.id, isAtDefault]);

	// Cleanup momentum animation on unmount
	useEffect(() => {
		return () => {
			if (momentumAnimation.current !== null) {
				cancelAnimationFrame(momentumAnimation.current);
			}
		};
	}, []);

	const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0) return;
		// Don't capture pointer if clicking on buttons
		if (
			event.target instanceof HTMLElement &&
			event.target.tagName === 'BUTTON'
		) {
			return;
		}
		const container = containerRef.current;
		if (!container) return;

		// Cancel any ongoing momentum animation
		if (momentumAnimation.current !== null) {
			cancelAnimationFrame(momentumAnimation.current);
			momentumAnimation.current = null;
		}

		container.setPointerCapture(event.pointerId);
		const now = performance.now();
		pointerState.current = {
			id: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			originX: viewport.x,
			originY: viewport.y,
			lastX: event.clientX,
			lastY: event.clientY,
			lastTime: now,
			velocityX: 0,
			velocityY: 0,
		};
		setIsDragging(true);
		setIsAtDefault(false);
	};

	const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!isDragging || pointerState.current.id !== event.pointerId) return;
		const now = performance.now();
		const dt = now - pointerState.current.lastTime;

		if (dt > 0) {
			const dx = event.clientX - pointerState.current.lastX;
			const dy = event.clientY - pointerState.current.lastY;
			pointerState.current.velocityX = dx / dt;
			pointerState.current.velocityY = dy / dt;
		}

		pointerState.current.lastX = event.clientX;
		pointerState.current.lastY = event.clientY;
		pointerState.current.lastTime = now;

		const deltaX = event.clientX - pointerState.current.startX;
		const deltaY = event.clientY - pointerState.current.startY;
		const newViewport = {
			...viewport,
			x: pointerState.current.originX + deltaX,
			y: pointerState.current.originY + deltaY,
		};
		setViewport(newViewport);
		setIsAtDefault(checkIfAtDefault(newViewport));
	};

	const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
		if (pointerState.current.id !== event.pointerId) return;
		const container = containerRef.current;
		if (container?.hasPointerCapture(event.pointerId)) {
			container.releasePointerCapture(event.pointerId);
		}

		// Start momentum animation if velocity is significant
		const velocityX = pointerState.current.velocityX;
		const velocityY = pointerState.current.velocityY;
		const speed = Math.sqrt(velocityX * velocityX + velocityY * velocityY);

		if (speed > 0.1) {
			startMomentum(velocityX, velocityY);
		}

		pointerState.current.id = null;
		setIsDragging(false);
	};

	const handleWheel = useCallback((event: WheelEvent) => {
		event.preventDefault();
		const container = containerRef.current;
		if (!container) return;

		// Cancel any ongoing momentum
		if (momentumAnimation.current !== null) {
			cancelAnimationFrame(momentumAnimation.current);
			momentumAnimation.current = null;
		}

		const rect = container.getBoundingClientRect();

		// Calculate zoom based on deltaY magnitude
		// Smaller deltas (touchpad) will zoom less, larger deltas (mouse wheel) will zoom more
		const zoomIntensity = 0.002;
		const delta = -event.deltaY * zoomIntensity;
		const scaleFactor = Math.exp(delta);

		setViewport((prev) => {
			const offsetX = (event.clientX - rect.left - prev.x) / prev.scale;
			const offsetY = (event.clientY - rect.top - prev.y) / prev.scale;
			const newScale = clamp(prev.scale * scaleFactor, MIN_SCALE, MAX_SCALE);
			const newX = event.clientX - rect.left - offsetX * newScale;
			const newY = event.clientY - rect.top - offsetY * newScale;
			const newViewport = { scale: newScale, x: newX, y: newY };
			setIsAtDefault(checkIfAtDefaultRef.current(newViewport));
			return newViewport;
		});
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		container.addEventListener('wheel', handleWheel, { passive: false });
		return () => container.removeEventListener('wheel', handleWheel);
	}, [handleWheel]);

	const handleReset = () => {
		// Cancel any ongoing momentum
		if (momentumAnimation.current !== null) {
			cancelAnimationFrame(momentumAnimation.current);
			momentumAnimation.current = null;
		}

		const initialViewport = getInitialViewport();
		pointerState.current = {
			id: null,
			startX: 0,
			startY: 0,
			originX: initialViewport.x,
			originY: initialViewport.y,
			lastX: 0,
			lastY: 0,
			lastTime: 0,
			velocityX: 0,
			velocityY: 0,
		};
		setIsDragging(false);
		setViewport(initialViewport);
		setIsAtDefault(true);
	};

	const startMomentum = (
		initialVelocityX: number,
		initialVelocityY: number,
	) => {
		let velocityX = initialVelocityX;
		let velocityY = initialVelocityY;
		const friction = 0.95; // Deceleration factor (iOS-like)
		const minVelocity = 0.1; // Stop when velocity is very small

		const animate = () => {
			// Apply friction
			velocityX *= friction;
			velocityY *= friction;

			// Check if we should stop
			const speed = Math.sqrt(velocityX * velocityX + velocityY * velocityY);
			if (speed < minVelocity) {
				momentumAnimation.current = null;
				return;
			}

			// Update viewport position
			setViewport((prev) => {
				const newViewport = {
					...prev,
					x: prev.x + velocityX * 16, // Scale by ~16ms per frame
					y: prev.y + velocityY * 16,
				};
				setIsAtDefault(checkIfAtDefault(newViewport));
				return newViewport;
			});

			// Continue animation
			momentumAnimation.current = requestAnimationFrame(animate);
		};

		momentumAnimation.current = requestAnimationFrame(animate);
	};

	const getTouchDistance = (touches: React.TouchList): number => {
		const dx = touches[0].clientX - touches[1].clientX;
		const dy = touches[0].clientY - touches[1].clientY;
		return Math.sqrt(dx * dx + dy * dy);
	};

	const getTouchCenter = (
		touches: React.TouchList,
	): { x: number; y: number } => {
		return {
			x: (touches[0].clientX + touches[1].clientX) / 2,
			y: (touches[0].clientY + touches[1].clientY) / 2,
		};
	};

	const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
		if (event.touches.length === 2) {
			event.preventDefault();

			// Cancel any ongoing momentum
			if (momentumAnimation.current !== null) {
				cancelAnimationFrame(momentumAnimation.current);
				momentumAnimation.current = null;
			}

			const distance = getTouchDistance(event.touches);
			const center = getTouchCenter(event.touches);
			setPinchState({
				initialDistance: distance,
				initialScale: viewport.scale,
				initialCenterX: center.x,
				initialCenterY: center.y,
				initialViewportX: viewport.x,
				initialViewportY: viewport.y,
			});
			setIsAtDefault(false);
		}
	};

	const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
		if (event.touches.length === 2 && pinchState) {
			event.preventDefault();
			const container = containerRef.current;
			if (!container) return;
			const rect = container.getBoundingClientRect();

			// Calculate scale change
			const distance = getTouchDistance(event.touches);
			const scaleFactor = distance / pinchState.initialDistance;
			const newScale = clamp(
				pinchState.initialScale * scaleFactor,
				MIN_SCALE,
				MAX_SCALE,
			);

			// Get current center point
			const currentCenter = getTouchCenter(event.touches);

			// Calculate the content point that was under the initial center
			const contentX = (pinchState.initialCenterX - rect.left - pinchState.initialViewportX) /
				pinchState.initialScale;
			const contentY = (pinchState.initialCenterY - rect.top - pinchState.initialViewportY) /
				pinchState.initialScale;

			// Calculate where that content point should be with the new scale, centered on current touch center
			const newX = currentCenter.x - rect.left - contentX * newScale;
			const newY = currentCenter.y - rect.top - contentY * newScale;

			const newViewport = { scale: newScale, x: newX, y: newY };
			setViewport(newViewport);
			setIsAtDefault(checkIfAtDefault(newViewport));
		}
	};

	const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
		if (event.touches.length < 2) {
			setPinchState(null);
		}
	};

	const stageTransform = {
		transform: `translate3d(${viewport.x}px, ${viewport.y}px, 0) scale(${viewport.scale})`,
		transformOrigin: '0 0',
	};

	const nodeHeightByOrder = useMemo(() => {
		return new Map(
			diagram.nodes.map((node) => [
				node.definition.order,
				getNodeHeight(node, activeFormat.diagramDimensions.nodeHeight),
			]),
		);
	}, [diagram.nodes, activeFormat.diagramDimensions.nodeHeight]);

	const renderedDiagramHeight = useMemo(() => {
		const maxNodeBottom = diagram.nodes.reduce((maxBottom, node) => {
			const nodeHeight = nodeHeightByOrder.get(node.definition.order) ??
				activeFormat.diagramDimensions.nodeHeight;
			return Math.max(maxBottom, node.definition.position.y + nodeHeight);
		}, 0);
		return Math.max(
			activeFormat.diagramDimensions.height,
			maxNodeBottom + activeFormat.diagramDimensions.rowUnit + 300,
		);
	}, [
		diagram.nodes,
		nodeHeightByOrder,
		activeFormat.diagramDimensions.height,
		activeFormat.diagramDimensions.nodeHeight,
		activeFormat.diagramDimensions.rowUnit,
	]);

	const edgePaths = useMemo(() => {
		return diagram.edges
			.filter((edge) => edge.definition.type === 'advance')
			.map((edge) => {
				const source = edge.source.definition.position;
				const target = edge.target.definition.position;
				const startX = source.x + activeFormat.diagramDimensions.nodeWidth;
				const sourceHeight = nodeHeightByOrder.get(edge.source.definition.order) ??
					activeFormat.diagramDimensions.nodeHeight;
				const targetHeight = nodeHeightByOrder.get(edge.target.definition.order) ??
					activeFormat.diagramDimensions.nodeHeight;
				const startY = source.y + sourceHeight / 2;
				const endX = target.x;
				const endY = target.y + targetHeight / 2;
				const midX = startX + (endX - startX) * 0.5;
				const path = `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
				return { edge, d: path };
			});
	}, [
		diagram.edges,
		activeFormat.diagramDimensions.nodeHeight,
		activeFormat.diagramDimensions.nodeWidth,
		nodeHeightByOrder,
	]);

	return (
		<div
			ref={containerRef}
			className='elim-diagram-container'
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			onPointerCancel={handlePointerUp}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
			role='presentation'
		>
			<div className='elim-diagram-toolbar'>
				<div className='elim-diagram-controls'>
					{!isAtDefault && (
						<>
							<button
								type='button'
								className='elim-reset-btn'
								onClick={handleReset}
							>
								Reset view
							</button>
							<div className='elim-diagram-zoom'>
								<span>{Math.round(viewport.scale * 100)}%</span>
							</div>
						</>
					)}
				</div>
			</div>
			<div className='elim-diagram-stage' style={stageTransform}>
				<svg
					className='elim-diagram-svg'
					width={activeFormat.diagramDimensions.width}
					height={renderedDiagramHeight}
					viewBox={`0 0 ${activeFormat.diagramDimensions.width} ${renderedDiagramHeight}`}
					role='img'
					aria-label='Double elimination bracket'
				>
					<defs>
						<filter id='shadow' x='-20%' y='-20%' width='140%' height='140%'>
							<feDropShadow
								dx='0'
								dy='4'
								stdDeviation='6'
								floodColor='rgba(0,0,0,0.2)'
							/>
						</filter>
					</defs>
					<g className='elim-edges'>
						{edgePaths.map(({ edge, d }) => (
							<path
								key={`${edge.definition.from}-${edge.definition.to}-${edge.definition.type}`}
								d={d}
								className={`elim-edge elim-edge--${edge.definition.type}`}
								data-state={edge.state}
								fill='none'
								pointerEvents='none'
							/>
						))}
					</g>
					<g className='elim-nodes'>
						{diagram.nodes.map((node) =>
							renderNode(
								node,
								currentRace,
								activeFormat.diagramDimensions.nodeWidth,
								nodeHeightByOrder.get(node.definition.order) ??
									activeFormat.diagramDimensions.nodeHeight,
							)
						)}
					</g>
				</svg>
			</div>
		</div>
	);
}

function renderNode(
	node: BracketNodeViewModel,
	currentRace: PBRaceRecord | null,
	nodeWidth: number,
	nodeHeight: number,
) {
	const { definition } = node;
	const isCurrentRace = currentRace ? node.raceIds.includes(currentRace.id) : false;
	const heatColumns = Array.from({ length: node.expectedHeatCount })
		.map(() => '1.3rem')
		.join(' ');
	const hasDestinationColumn = node.slots.some(
		(slot) => slot.destinationLabel != null,
	);
	const slotGridTemplateColumns = `1.4rem 2rem minmax(0, 1fr) ${heatColumns} 1.6rem ${
		hasDestinationColumn ? DESTINATION_COLUMN_WIDTH : '0'
	}`;
	return (
		<g
			key={definition.order}
			className='elim-node'
			transform={`translate(${definition.position.x} ${definition.position.y})`}
		>
			<rect
				x={0}
				y={0}
				rx={12}
				ry={12}
				width={nodeWidth}
				height={nodeHeight}
				className='elim-node-rect'
				data-status={node.status}
				data-current={isCurrentRace ? 'true' : 'false'}
				filter='url(#shadow)'
			/>
			<foreignObject x={0} y={0} width={nodeWidth} height={nodeHeight}>
				<div className='elim-node-card' data-status={node.status}>
					<header>
						<div className='elim-node-title'>{node.headline}</div>
						<div className='elim-node-sub'>{node.subline}</div>
					</header>
					{node.expectedHeatCount > 0 && (
						<div
							className='elim-node-heats-header'
							style={{
								gridTemplateColumns: slotGridTemplateColumns,
							}}
						>
							<span aria-hidden='true' />
							<span aria-hidden='true' />
							<span aria-hidden='true' />
							{Array.from({ length: node.expectedHeatCount }).map(
								(_, index) => (
									<span key={`${definition.order}-h-${index}`}>
										H{index + 1}
									</span>
								),
							)}
							<span>Σ</span>
							<span aria-hidden='true' />
						</div>
					)}
					<ul>
						{node.slots.map((slot) => (
							<li
								key={slot.id}
								className='elim-node-slot'
								data-winner={slot.isWinner ? 'true' : 'false'}
								data-eliminated={slot.isEliminated ? 'true' : 'false'}
								data-predicted={slot.isPredicted ? 'true' : 'false'}
								style={{
									gridTemplateColumns: slotGridTemplateColumns,
								}}
							>
								<span className='slot-position'>{slot.position ?? '–'}</span>
								<span className='slot-channel'>{slot.channelLabel}</span>
								<span className='slot-name' title={slot.name}>
									{slot.name}
								</span>
								{slot.heatPoints.map((heatPoints, heatIndex) => (
									<span
										key={`${slot.id}-heat-${heatIndex}`}
										className='slot-heat-points'
									>
										{heatPoints ?? '·'}
									</span>
								))}
								<span className='slot-total-points'>
									{slot.totalPoints ?? '·'}
								</span>
								{slot.destinationLabel && (
									<span className='slot-destination'>
										{slot.destinationLabel}
									</span>
								)}
							</li>
						))}
					</ul>
				</div>
			</foreignObject>
		</g>
	);
}
